import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import classNames from 'classnames';

import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { IScheduledEmail, Channel, DISPLAY_REGISTRATION_CONFIRMATION_EMAIL, EmailTypeEnum, IScheduledEmailCustomizations, LanguagesAbbr, Registration, IScheduledEmailContent, IScheduledEmailSettings, SessionTypesEnum } from '../../../../types/working-model';
import { ParamsProps } from '../../../live-event/live-event';
import { editScheduledEmailInAdmin, setDisplayScheduledEmail, updateRegistrationSettings } from '../../../../store/actions/admin/create-event';
import { ScheduledEmail } from './create-email';
import { EmailTextPhrases } from './event-emails';
import { EditScheduledEmailWorkingContent } from '../../../../connection/scheduled-emails';
import { showAlert } from '../../../general-ui/alert/alert-service';
import Icon, { COLORS, ICONS } from '../../../general-ui/icon';
import ScheduleEmailModal from '../homepage/navigation/panel/emails/schedule-email-modal';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { defaultEmailSettings, defaultEmailTimezone } from '../../../../store/utils/create-event';


export const mockRegistrationEmail = (channel: number, registrationConfirmationEmail: IScheduledEmailCustomizations, registrationEmailSettings?: IScheduledEmailSettings, registrationEmailTimezone?: string): IScheduledEmail => {
	return {
		email_type: EmailTypeEnum.event_registration_email,
		uuid: 'mock',
		audiences: [],
		status: 0,
		remote_job_id: 'mock',
		event_uuid: 'mock',
		channel: channel,
		delivery_date: 2146032000000, //year 2038
		customizations: registrationConfirmationEmail,
		sent: false,
		language: null,
		failed_reason: null,
		settings: registrationEmailSettings ?? defaultEmailSettings,
		timezone: registrationEmailTimezone ?? defaultEmailTimezone,
	};
};

export const DisplayEmail = (): JSX.Element => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const scheduledEmails = useTypedSelector(state => state.CreateEventReducer.scheduledEmails?.scheduledEmails);
	const displayEmail = useTypedSelector(state => state.CreateEventReducer.scheduledEmails?.displayEmail);
	const saving = useTypedSelector(state => state.CreateEventReducer.saving);
	const activeChannel = useTypedSelector(state => state.AuthReducer.user?.active_channel);
	const channels = useTypedSelector(state => state.AuthReducer.channels);
	const publishedUrl = useTypedSelector(state => state.CreateEventReducer.publishedUrl);
	const loadingEmails = useTypedSelector(state => state.CreateEventReducer.scheduledEmails?.loadingEmails);

	const { id, language: paramLanguage } = useParams<ParamsProps>();
	const { pathname } = useLocation();
	const dispatch = useDispatch();

	const [email, setEmail] = useState<IScheduledEmail | null>(null);
	const [emailTranslation, setEmailTranslation] = useState<EmailTextPhrases | null>(null);
	const [openScheduleEmailModal, setOpenScheduleEmailModal] = useState(false);

	const currentChannel: Channel | undefined = useMemo(() => {
		return channels.find((channel) => channel.channel === activeChannel);
	}, [channels, activeChannel]);

	const mockScheduleEmailWithRegistrationEmail = useCallback((registrationConfirmationEmail: IScheduledEmailCustomizations, registrationEmailSettings?: IScheduledEmailSettings, registrationEmailTimezone?: string): IScheduledEmail | undefined => {
		if (!currentChannel?.channel || !registrationConfirmationEmail) return;
		return mockRegistrationEmail(currentChannel.channel, registrationConfirmationEmail, registrationEmailSettings, registrationEmailTimezone);
	}, [currentChannel]);

	const emailLanguage: LanguagesAbbr = useMemo(() => {
		if (email?.language) {
			return email.language;
		}
		return paramLanguage;
	}, [email?.language, paramLanguage]);

	useEffect(() => {
		const fetchTranslation = async () => {
			try {
				// clear previous at each language change so component won't render until new translation json is fetched
				setEmailTranslation(null);
				const emailtranslations = await import(`../../../../i18n/${emailLanguage}/email.json`);
				setEmailTranslation(emailtranslations);
			} catch {
				console.log("Error: Can't get translation file.");
			}
		};
		fetchTranslation();
	}, [emailLanguage]);

	const onEdit = async (value: string | IScheduledEmailContent['session_image'], key: string) => {
		if (!email || !emailLanguage || !token) return;

		// this will handle if someone pastes in text and trys to save it. Otherwise they wont be able to type past 65 characters
		if (key === 'button' && typeof value === 'string' && value.length > 65) {
			showAlert({
				type: 'error',
				description: 'Error updating content. Max button text is 65 characters'
			});
			return;
		}
		const updatedCustomizations = {
			...(email?.working_customizations ?? email?.customizations),
			[emailLanguage]: {
				...(email?.working_customizations?.[emailLanguage] ?? email.customizations[emailLanguage]),
				[key]: value,
			}
		};
		const updatedEmail: IScheduledEmail = {
			...email,
			working_customizations: updatedCustomizations
		};
		setEmail(updatedEmail);
		// if updating reg email, we need to update registration settings
		if (email.email_type === EmailTypeEnum.event_registration_email) {
			if (!workingEvent?.registration_settings || !token) return;

			const updatedRegistrationSettings: Registration = {
				...workingEvent.registration_settings,
				registration_confirmation_email: updatedCustomizations
			};
			return dispatch(updateRegistrationSettings(token, workingEvent?.uuid, updatedRegistrationSettings));
		}

		// we are only updating working customizations, actual customizations gets updated on Publish
		try {
			await EditScheduledEmailWorkingContent(token, updatedEmail);
			dispatch(editScheduledEmailInAdmin(updatedEmail));
		} catch (e) {
			console.error(e);
			showAlert({
				type: 'error',
				description: 'Error updating content. Please refresh and try again.'
			});
		}
	};

	useEffect(() => {
		// changing email colors triggers a save event which will fire this useEffect,
		// but we dont want to actually fetch the email yet cause the DB wont be updated with new colors,
		// so we need to wait for saving to end (also triggering this useEffect) which will happen once the api responds
		if (saving) {
			return;
		}
		// only need this if we are not on the details view
		const firstUnsentEmail = !displayEmail && scheduledEmails?.find(schedule => !schedule.sent);
		// if we are on a scheduled email details view
		if (token && id && displayEmail) {
			// if display email is a string of registration-confirmation, then display the events registration confirmation
			if (displayEmail === DISPLAY_REGISTRATION_CONFIRMATION_EMAIL) {
				const registrationConfirmationEmail = workingEvent?.registration_settings?.registration_confirmation_email;
				const registrationEmailSettings = workingEvent?.registration_settings?.registration_email_settings;
				const registrationEmailTimezone = workingEvent?.registration_settings?.registration_email_timezone;

				const mockScheduleEmail = mockScheduleEmailWithRegistrationEmail(registrationConfirmationEmail ?? {}, registrationEmailSettings, registrationEmailTimezone);

				return mockScheduleEmail && setEmail(mockScheduleEmail);
			}
			const emailToDisplay = scheduledEmails?.find(schedule => schedule.uuid === displayEmail);
			emailToDisplay && setEmail(emailToDisplay);
		} else if (firstUnsentEmail && token && scheduledEmails?.length) {
			// get the next scheduled email and display that
			const nextEmailToSend = scheduledEmails.reduce((a: IScheduledEmail, c: IScheduledEmail) => {
				if (!c.sent && c.delivery_date < a.delivery_date) return c;
				return a;
			}, firstUnsentEmail);

			nextEmailToSend && dispatch(setDisplayScheduledEmail(nextEmailToSend.uuid));
		} else if (workingEvent?.registration_on && workingEvent?.registration_settings?.sendEmailConfirmation) {
			// display registration email by default. Only if registration is on tho
			const registrationConfirmationEmail = workingEvent?.registration_settings?.registration_confirmation_email;
			const registrationEmailSettings = workingEvent?.registration_settings?.registration_email_settings;
			const registrationEmailTimezone = workingEvent?.registration_settings?.registration_email_timezone;

			const mockScheduleEmail = mockScheduleEmailWithRegistrationEmail(registrationConfirmationEmail ?? {}, registrationEmailSettings, registrationEmailTimezone);

			mockScheduleEmail && setEmail(mockScheduleEmail);
		} else {
			setEmail(null);
		}

		// we need to rerun this if emailColors or hideBrandliveLogo changes
	}, [
		id,
		token,
		pathname,
		displayEmail,
		scheduledEmails,
		saving,
		dispatch,
		paramLanguage,
		workingEvent?.registration_settings,
		currentChannel?.channel,
		mockScheduleEmailWithRegistrationEmail,
		workingEvent?.registration_on,
		workingEvent?.registration_settings?.sendEmailConfirmation,
	]);

	const isAllOnDemand = () => {
		return !workingEvent?.sessions.find(session => session.session_type !== SessionTypesEnum.onDemand);
	};

	const isOnDemandSession = () => {
		const session = workingEvent?.sessions.find(session => session.uuid === email?.session_uuid);
		if (session && session.session_type === SessionTypesEnum.onDemand) {
			return true;
		}

		return false;
	};

	if (!workingEvent || !currentChannel) return (<></>);

	if (!!email && !!emailTranslation) {
		return (
			<div className={classNames({ "disable-editing": email?.sent })} tabIndex={email?.sent ? 0 : undefined}>
				<ScheduledEmail
					emailType={email.email_type}
					language={emailLanguage}
					workingEvent={workingEvent}
					currentChannel={currentChannel}
					publishedUrl={publishedUrl ?? ""}
					emailTranslation={emailTranslation}
					sessionUUID={email.session_uuid}
					emailSendTime={email.delivery_date}
					customContent={email.working_customizations?.[emailLanguage] ?? email.customizations?.[emailLanguage]}
					customSettings={email.settings}
					onEdit={onEdit}
					timezone={email.timezone}
					idx={email.uuid}
					sent={email.sent}
					hideCalendar={isAllOnDemand() || isOnDemandSession()}
				/>
			</div>
		);
	}

	return (
		<div className="add-new-container no-scheduled-emails">
			<div>
				<TypographyItem tagName='h3'>
					No scheduled emails
				</TypographyItem>
			</div>
			<button
				className="no-style add-invitation-button session-option-container add-custom-section"
				onClick={() => setOpenScheduleEmailModal(true)}
				disabled={loadingEmails}
			>
				<Icon name={ICONS.ADD} size={16} color={COLORS.CYAN} />
				Add New
			</button>
			<ScheduleEmailModal
				open={openScheduleEmailModal}
				setOpen={setOpenScheduleEmailModal}
				onClose={() => null}
			/>
		</div>
	);
};